// src/themes/modern/restaurant/ModernRestaurantPage.tsx
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBookOpen,
  FaFilter,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaSearch,
  FaSlidersH,
  FaTimes,
  FaComment,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import Chatbot from "~/chatbot/components/Chatbot";
import MenuItemDetailsModal from "~/templates/modern/menu/components/MenuItemDetailsModal";
import type { BranchData, MenuItem } from "~/types/new_types";
import RestaurantInfo from "./components/RestaurantInfo";
import RestaurantLocation from "./components/RestaurantLocation";
import BranchFeedback from "./components/BranchFeedback";
import FeedbackModal from "~/components/feedback/FeedbackModal";
import FeedbackCongratulations from "~/components/feedback/FeedbackCongratulations";

import RestaurantLayout from "~/pages/restaurant/RestaurantLayout";
import { useBranch } from "~/contexts/BranchContext";
import { createPortal } from "react-dom";
import MenuItemGrid from "../menu/components/MenuItemGrid";
import MenuItemList from "../menu/components/MenuItemList";
import ViewModeSwitcher from "../menu/components/ViewModeSwitcher";
import { useViewMode } from "~/contexts/ViewModeContext";
import { useDropdownPosition } from "~/hooks/useDropdownPosition";
import CartButton from "~/cart/Components/CartButton";
import { RestaurantOnePageLayout } from "~/pages/restaurant/one-page-layout";
import { FaX } from "react-icons/fa6";

type Props = {
  branchData: BranchData;
};

const ModernRestaurantPage: React.FC<Props> = ({ branchData }) => {
  const { t, i18n } = useTranslation();
  const { viewMode, setViewMode } = useViewMode();
  const [activeTab, setActiveTab] = useState<
    "info" | "menu" | "reviews" | "location" | "feedback"
  >("menu");
  const [showChatbotHint, setShowChatbotHint] = useState(false);
  const [randomHint, setRandomHint] = useState("");
  const [feedbackItem, setFeedbackItem] = useState<MenuItem | null>(null);
  const [showFeedbackCongratulations, setShowFeedbackCongratulations] =
    useState(false);

  // Helper to get localized category name (Arabic or English)
  const getCategoryName = (category: any) => {
    return i18n.language === "ar" && category.ar?.name
      ? category.ar.name
      : category.name;
  };
  const isOnePageLayout = branchData.branch.theme.isOnePageLayout === true;
  const isAgentActive = branchData.summary.agent_info?.some(
    (agent) => agent.type === "client" && agent.is_active === true,
  );

  const tabs = [
    { id: "menu", label: t("restaurant.tabs.menu"), icon: <FaBookOpen /> },
    { id: "info", label: t("restaurant.tabs.info"), icon: <FaInfoCircle /> },
    {
      id: "feedback",
      label: t("restaurant.tabs.feedback"),
      icon: <FaComment />,
    },
  ];
  if (branchData?.branch?.location) {
    tabs.push({
      id: "location",
      label: t("restaurant.tabs.location"),
      icon: <FaMapMarkerAlt />,
    });
  }
  const {
    theme,
    hasActiveFilters,
    handleDietaryFilterChange,
    clearFilters,
    dietaryFilters,
    filteredItems,
    globalSearchQuery,
    setGlobalSearchQuery,
    selectedItem,
    setSelectedItem,
    selectedCategoryFilter,
    setSelectedCategoryFilter,
    dietaryPreferences,
  } = useBranch();

  // Get categoryId from URL params (used in menu/category logic)
  const { categoryId } = useParams();

  // Category filter state and refs
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const categoryFilterButtonRef = useRef<HTMLButtonElement | null>(null);
  // Dietary preferences filter state and refs
  const [showDietaryFilter, setShowDietaryFilter] = useState(false);
  const dietaryFilterButtonRef = useRef<HTMLButtonElement | null>(null);
  // Search input ref for keyboard focus
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus search input when user starts typing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input, textarea, or contenteditable element
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      // Ignore if chatbot is open
      const chatbotContainer = document.querySelector(
        ".agent-chatbot-container",
      );
      if (chatbotContainer) {
        return;
      }

      // Ignore special keys (Ctrl, Alt, Meta, Shift, Escape, Tab, etc.)
      if (
        e.ctrlKey ||
        e.altKey ||
        e.metaKey ||
        e.key === "Escape" ||
        e.key === "Tab" ||
        e.key === "Enter" ||
        e.key === "Shift" ||
        e.key === "Control" ||
        e.key === "Alt" ||
        e.key === "Meta"
      ) {
        return;
      }

      // If it's a printable character, focus the search input
      if (e.key.length === 1) {
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Show chatbot hint after delay when agent is active
  useEffect(() => {
    if (isAgentActive) {
      const timer = setTimeout(() => {
        // Get random hint from translations
        const hints = t("chatbot.hints", { returnObjects: true }) as string[];
        if (hints && Array.isArray(hints)) {
          const randomIndex = Math.floor(Math.random() * hints.length);
          setRandomHint(hints[randomIndex]);
        }
        setShowChatbotHint(true);
      }, 3000); // Show hint after 3 seconds

      // Auto-hide after 8 seconds
      const hideTimer = setTimeout(() => {
        setShowChatbotHint(false);
      }, 11000);

      return () => {
        clearTimeout(timer);
        clearTimeout(hideTimer);
      };
    }
  }, [isAgentActive, t]);

  // Prevent page scroll when dropdowns are open
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (showCategoryFilter || showDietaryFilter) {
        e.preventDefault();
      }
    };

    if (showCategoryFilter || showDietaryFilter) {
      document.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, [showCategoryFilter, showDietaryFilter]);

  // Use the custom hook for both dropdowns
  const categoryDropdown = useDropdownPosition({
    isOpen: showCategoryFilter,
    buttonRef: categoryFilterButtonRef,
    onClose: () => setShowCategoryFilter(false),
    dropdownWidth: Math.min(window.innerWidth * 0.9, 320),
    dropdownHeight: Math.min(window.innerHeight * 0.7, 400),
  });
  const dietaryDropdown = useDropdownPosition({
    isOpen: showDietaryFilter,
    buttonRef: dietaryFilterButtonRef,
    onClose: () => setShowDietaryFilter(false),
    dropdownWidth: Math.min(window.innerWidth * 0.9, 320),
    dropdownHeight: Math.min(window.innerHeight * 0.5, 300),
  });

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: branchData.branch.theme?.backgroundColor,
        color: branchData.branch.theme.textColor,
        fontFamily: branchData.branch.theme.fontFamily,
      }}
    >
      <MenuItemDetailsModal
        item={selectedItem!}
        show={!!selectedItem}
        close={() => setSelectedItem(null)}
        theme={branchData.branch.theme}
        onFeedback={(item) => setFeedbackItem(item)}
      />

      {/* Tabs Navigation - Centered */}
      <nav
        className="top-0 left-0 right-0 z-40 backdrop-blur-md shadow-lg py-4 px-2"
        style={{
          backgroundColor: `${branchData.branch.theme.backgroundColor}CC`,
          borderBottom: `1px solid ${branchData.branch.theme.borderColor}20`,
        }}
      >
        <div className="container mx-auto px-4">
          {/* Center tabs when they fit; allow natural left-start scrolling when they overflow */}
          <div className="w-full overflow-x-auto no-scrollbar">
            <div className="flex w-max min-w-full flex-nowrap justify-center gap-2 whitespace-nowrap px-1">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                // Ensure textColor has proper hex format
                const textColor = branchData.branch.theme.textColor?.startsWith(
                  "#",
                )
                  ? branchData.branch.theme.textColor
                  : `#${branchData.branch.theme.textColor || "1e293b"}`;

                return (
                  <motion.button
                    key={tab.id}
                    onClick={() =>
                      setActiveTab(
                        tab.id as "info" | "menu" | "reviews" | "location",
                      )
                    }
                    className="flex items-center gap-2 py-3 px-4 min-w-fit rounded-full transition-colors cursor-pointer"
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                    style={{
                      backgroundColor: isActive
                        ? branchData.branch.theme.primaryColor
                        : `${branchData.branch.theme.primaryColor}15`,
                      color: isActive ? "#ffffff" : textColor,
                      borderRadius: branchData.branch.theme.borderRadius,
                    }}
                  >
                    {tab.icon}
                    <span className="text-sm font-medium">{tab.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>
      </nav>

      {/* Search Bar and Filters - Only shown on menu tab */}
      {activeTab === "menu" && (
        <div
          className="sticky top-0 z-30 backdrop-blur-md shadow-md py-3 px-2"
          style={{
            backgroundColor: `${branchData.branch.theme.backgroundColor}F5`,
            borderBottom: `1px solid ${branchData.branch.theme.borderColor}20`,
          }}
        >
          <div className="container mx-auto px-4">
            {/* Search Bar with Category Filter */}
            <div className="flex gap-2 w-full">
              <div className="relative flex-grow w-full">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={globalSearchQuery}
                  onChange={(e) => setGlobalSearchQuery(e.target.value)}
                  placeholder={t("menu.searchPlaceholder")}
                  disabled={!!selectedItem}
                  className="w-full px-4 py-3 ps-10 pe-16 rounded-lg transition-all duration-300 focus:ring-2 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  style={
                    {
                      backgroundColor: theme?.cardColor,
                      color: theme?.textColor,
                      borderRadius: theme?.borderRadius,
                      borderColor: `${theme?.borderColor}40`,
                      "--tw-ring-color": `${theme?.primaryColor}40`,
                    } as React.CSSProperties
                  }
                />

                {/* Prefix search icon */}
                <FaSearch className="absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-400" />

                {/* Clear (X) button when search query exists */}
                {globalSearchQuery && (
                  <button
                    onClick={() => setGlobalSearchQuery("")}
                    className="absolute end-9 top-1/2 transform -translate-y-1/2 hover:text-gray-600 transition-colors cursor-pointer"
                    style={{ color: `${theme?.textColor}60` }}
                  >
                    <FaTimes />
                  </button>
                )}
                {/* Category filter button */}
                <button
                  ref={categoryFilterButtonRef}
                  onClick={() => setShowCategoryFilter(!showCategoryFilter)}
                  className="absolute end-3 top-1/2 transform -translate-y-1/2 hover:text-gray-400 transition-colors"
                  style={{ color: `${theme?.textColor}80` }}
                >
                  <FaFilter />
                </button>
              </div>
              {/* Category Filter Dropdown */}
              {showCategoryFilter &&
                categoryDropdown.isPositionReady &&
                createPortal(
                  <AnimatePresence>
                    <motion.div
                      ref={categoryDropdown.dropdownRef}
                      initial={{
                        opacity: 0,
                        scale: 0.95,
                        y: categoryDropdown.position.openUpward ? 6 : -6,
                      }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{
                        opacity: 0,
                        scale: 0.95,
                        y: categoryDropdown.position.openUpward ? 6 : -6,
                      }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                      className="fixed z-[9998] shadow-xl"
                      style={{
                        top: categoryDropdown.position.openUpward
                          ? "auto"
                          : `${categoryDropdown.position.top}px`,
                        bottom: categoryDropdown.position.openUpward
                          ? `${
                              window.innerHeight - categoryDropdown.position.top
                            }px`
                          : "auto",
                        left: `${categoryDropdown.position.left}px`,
                        width: "min(90vw, 320px)",
                        backgroundColor: theme?.cardColor,
                        borderRadius: theme?.borderRadius,
                        boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                        maxHeight: "min(70vh, 400px)",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Header - Fixed */}
                      <div
                        className="flex justify-between items-center px-4 py-3 border-b flex-shrink-0"
                        style={{
                          borderColor: `${theme?.borderColor}20`,
                          backgroundColor: theme?.cardColor,
                          borderTopLeftRadius: theme?.borderRadius,
                          borderTopRightRadius: theme?.borderRadius,
                        }}
                      >
                        <h3
                          className="font-semibold text-sm md:text-base"
                          style={{ color: theme?.textColor }}
                        >
                          {t("menu.categories")}
                        </h3>
                        {selectedCategoryFilter && (
                          <motion.button
                            onClick={() => setSelectedCategoryFilter(null)}
                            className="text-xs flex items-center gap-1 px-2 py-1 rounded cursor-pointer"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            style={{
                              backgroundColor: `${theme?.secondaryColor}20`,
                              color: theme?.secondaryColor,
                            }}
                          >
                            <FaTimes size={10} />
                            <span className="hidden sm:inline">
                              {t("menu.clearFilters")}
                            </span>
                          </motion.button>
                        )}
                      </div>

                      {/* Scrollable Content */}
                      <div
                        className="flex flex-col gap-2 p-3 overflow-y-auto flex-1"
                        onWheel={(e) => {
                          // Prevent page scroll when scrolling inside dropdown
                          const target = e.currentTarget;
                          const isScrollable =
                            target.scrollHeight > target.clientHeight;

                          if (isScrollable) {
                            const isAtTop = target.scrollTop === 0;
                            const isAtBottom =
                              target.scrollTop + target.clientHeight >=
                              target.scrollHeight - 1;

                            // Prevent page scroll if we're not at the boundaries
                            // or if we're at boundary and scrolling in the opposite direction
                            if (
                              (!isAtTop && e.deltaY < 0) ||
                              (!isAtBottom && e.deltaY > 0)
                            ) {
                              e.stopPropagation();
                            }
                          }
                        }}
                        style={{
                          scrollbarWidth: "thin",
                          scrollbarColor: `${theme?.primaryColor}40 transparent`,
                          borderBottomLeftRadius: theme?.borderRadius,
                          borderBottomRightRadius: theme?.borderRadius,
                        }}
                      >
                        <motion.button
                          onClick={() => {
                            setSelectedCategoryFilter(null);
                            setShowCategoryFilter(false);
                          }}
                          className="px-3 py-2.5 rounded-lg text-sm text-left transition-all flex items-center justify-between"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          style={{
                            backgroundColor:
                              selectedCategoryFilter === null
                                ? theme?.primaryColor
                                : `${theme?.primaryColor}10`,
                            color:
                              selectedCategoryFilter === null
                                ? "#fff"
                                : theme?.textColor,
                            borderRadius: theme?.borderRadius,
                            fontWeight:
                              selectedCategoryFilter === null ? "600" : "400",
                          }}
                        >
                          <span>{t("menu.allCategories")}</span>
                          {selectedCategoryFilter === null && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-2 h-2 rounded-full bg-white"
                            />
                          )}
                        </motion.button>

                        {branchData.categories.map((category) => (
                          <motion.button
                            key={category.id}
                            onClick={() => {
                              setSelectedCategoryFilter(category.id);
                              setShowCategoryFilter(false);
                            }}
                            className="px-3 py-2.5 rounded-lg text-sm text-left transition-all flex items-center justify-between"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            style={{
                              backgroundColor:
                                selectedCategoryFilter === category.id
                                  ? theme?.primaryColor
                                  : `${theme?.primaryColor}10`,
                              color:
                                selectedCategoryFilter === category.id
                                  ? "#fff"
                                  : theme?.textColor,
                              borderRadius: theme?.borderRadius,
                              fontWeight:
                                selectedCategoryFilter === category.id
                                  ? "600"
                                  : "400",
                            }}
                          >
                            <span className="truncate">
                              {getCategoryName(category)}
                            </span>
                            {selectedCategoryFilter === category.id && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="w-2 h-2 rounded-full bg-white flex-shrink-0 ml-2"
                              />
                            )}
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  </AnimatePresence>,
                  document.body,
                )}

              {/* Dietary_preferences Filter Button & Dropdown - Only show when NOT in one page layout */}
              {!isOnePageLayout && (
                <div className="flex gap-2 items-center relative">
                  <motion.button
                    ref={dietaryFilterButtonRef}
                    onClick={() => setShowDietaryFilter((prev) => !prev)}
                    className="flex items-center gap-2 px-4 py-3 rounded-lg cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      backgroundColor: showDietaryFilter
                        ? theme?.primaryColor
                        : `${theme?.primaryColor}20`,
                      color: showDietaryFilter ? "#fff" : theme?.textColor,
                      borderRadius: theme?.borderRadius,
                    }}
                  >
                    <FaSlidersH />
                    <span className="hidden md:inline">
                      {t("menu.filters")}
                    </span>

                    {hasActiveFilters() && (
                      <span
                        className="flex h-5 w-5 items-center justify-center text-xs rounded-full text-white"
                        style={{ backgroundColor: theme?.secondaryColor }}
                      >
                        {Object.values(dietaryFilters).filter(Boolean).length +
                          (globalSearchQuery ? 1 : 0)}
                      </span>
                    )}
                  </motion.button>

                  {/* Dietary Dropdown */}
                  {showDietaryFilter &&
                    dietaryDropdown.isPositionReady &&
                    createPortal(
                      <AnimatePresence>
                        <motion.div
                          ref={dietaryDropdown.dropdownRef}
                          initial={{
                            opacity: 0,
                            scale: 0.95,
                            y: dietaryDropdown.position.openUpward ? 6 : -6,
                          }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{
                            opacity: 0,
                            scale: 0.95,
                            y: dietaryDropdown.position.openUpward ? 6 : -6,
                          }}
                          transition={{ duration: 0.15, ease: "easeOut" }}
                          className="fixed z-[9999] shadow-xl"
                          style={{
                            top: dietaryDropdown.position.openUpward
                              ? "auto"
                              : `${dietaryDropdown.position.top}px`,
                            bottom: dietaryDropdown.position.openUpward
                              ? `${
                                  window.innerHeight -
                                  dietaryDropdown.position.top
                                }px`
                              : "auto",
                            left: `${dietaryDropdown.position.left}px`,
                            width: "min(90vw, 320px)",
                            backgroundColor: theme?.cardColor,
                            borderTopLeftRadius: theme?.borderRadius,
                            borderTopRightRadius: theme?.borderRadius,
                          }}
                        >
                          {/* Header - Fixed */}
                          <div
                            className="flex justify-between items-center px-4 py-3 border-b flex-shrink-0"
                            style={{
                              borderColor: `${theme?.borderColor}20`,
                              backgroundColor: theme?.cardColor,
                            }}
                          >
                            <h3
                              className="font-semibold text-sm md:text-base"
                              style={{ color: theme?.textColor }}
                            >
                              {t("menu.dietaryPreferences")}
                            </h3>

                            {hasActiveFilters() && (
                              <motion.button
                                onClick={clearFilters}
                                className="text-xs flex items-center gap-1 px-2 py-1 rounded cursor-pointer"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                  backgroundColor: `${theme?.secondaryColor}20`,
                                  color: theme?.secondaryColor,
                                }}
                              >
                                <FaTimes size={10} />
                                <span className="hidden sm:inline">
                                  {t("menu.clearFilters")}
                                </span>
                              </motion.button>
                            )}
                          </div>

                          <div
                            className="p-4 overflow-y-auto flex-1"
                            onWheel={(e) => {
                              // Prevent page scroll when scrolling inside dropdown
                              const target = e.currentTarget;
                              const isScrollable =
                                target.scrollHeight > target.clientHeight;

                              if (isScrollable) {
                                const isAtTop = target.scrollTop === 0;
                                const isAtBottom =
                                  target.scrollTop + target.clientHeight >=
                                  target.scrollHeight - 1;

                                // Prevent page scroll if we're not at the boundaries
                                // or if we're at boundary and scrolling in the opposite direction
                                if (
                                  (!isAtTop && e.deltaY < 0) ||
                                  (!isAtBottom && e.deltaY > 0)
                                ) {
                                  e.stopPropagation();
                                }
                              }
                            }}
                            style={{
                              scrollbarWidth: "thin",
                              scrollbarColor: `${theme?.primaryColor}40 transparent`,
                              borderBottomLeftRadius: theme?.borderRadius,
                              borderBottomRightRadius: theme?.borderRadius,
                            }}
                          >
                            <div className="flex flex-col gap-2.5">
                              {dietaryPreferences &&
                              dietaryPreferences.length > 0
                                ? dietaryPreferences.map((pref) => (
                                    <motion.button
                                      key={pref.id}
                                      onClick={() =>
                                        handleDietaryFilterChange(pref)
                                      }
                                      className="w-full px-4 py-2.5 rounded-lg flex items-center justify-between cursor-pointer transition-all"
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      style={{
                                        backgroundColor: dietaryFilters.some(
                                          (f) => f.id === pref.id,
                                        )
                                          ? theme?.primaryColor
                                          : `${theme?.primaryColor}10`,
                                        color: dietaryFilters.some(
                                          (f) => f.id === pref.id,
                                        )
                                          ? "#fff"
                                          : theme?.textColor,
                                        borderRadius: theme?.borderRadius,
                                        fontWeight: dietaryFilters.some(
                                          (f) => f.id === pref.id,
                                        )
                                          ? "600"
                                          : "400",
                                      }}
                                    >
                                      <span>
                                        {i18n.language === "ar" && pref.ar?.name
                                          ? pref.ar.name
                                          : pref.name}
                                      </span>
                                      {dietaryFilters.some(
                                        (f) => f.id === pref.id,
                                      ) && (
                                        <motion.div
                                          initial={{ scale: 0, rotate: -180 }}
                                          animate={{ scale: 1, rotate: 0 }}
                                          className="w-2 h-2 rounded-full bg-white"
                                        />
                                      )}
                                    </motion.button>
                                  ))
                                : null}
                            </div>
                          </div>
                        </motion.div>
                      </AnimatePresence>,
                      document.body,
                    )}
                  {categoryId && (
                    <ViewModeSwitcher
                      currentMode={viewMode}
                      onModeChange={setViewMode}
                      theme={branchData?.branch?.theme!}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {(selectedCategoryFilter || hasActiveFilters()) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="sticky top-0 z-1 py-3 px-4 backdrop-blur-md border-b"
          style={{
            backgroundColor: `${branchData.branch.theme.backgroundColor}F0`,
            borderColor: `${branchData.branch.theme.borderColor}20`,
          }}
        >
          <div className="container  mx-auto flex flex-wrap items-center gap-2">
            <span
              className="text-sm font-medium opacity-70"
              style={{ color: theme?.textColor }}
            >
              {t("activeFilters")}:
            </span>

            {/* Selected Category Chip */}
            {selectedCategoryFilter && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
                style={{
                  backgroundColor: `${theme?.primaryColor}20`,
                  color: theme?.primaryColor,
                  border: `1px solid ${theme?.primaryColor}40`,
                }}
              >
                <FaFilter size={10} />
                <span className="font-medium">
                  {getCategoryName(
                    branchData.categories.find(
                      (cat) => cat.id === selectedCategoryFilter,
                    ),
                  ) || "Category"}
                </span>
                <button
                  onClick={() => setSelectedCategoryFilter(null)}
                  className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                >
                  <FaTimes size={10} />
                </button>
              </motion.div>
            )}

            {/* Dietary Filter Chips */}
            {dietaryPreferences && dietaryPreferences.length > 0
              ? dietaryFilters.map((selectedPref) => (
                  <motion.div
                    key={selectedPref.id}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
                    style={{
                      backgroundColor: `${theme?.secondaryColor}20`,
                      color: theme?.secondaryColor,
                      border: `1px solid ${theme?.secondaryColor}40`,
                    }}
                  >
                    <span className="font-medium">
                      {i18n.language === "ar" && selectedPref.ar?.name
                        ? selectedPref.ar.name
                        : selectedPref.name}
                    </span>
                    <button
                      onClick={() => handleDietaryFilterChange(selectedPref)}
                      className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                    >
                      <FaTimes size={10} />
                    </button>
                  </motion.div>
                ))
              : null}

            {/* Clear All Button */}
            <motion.button
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setSelectedCategoryFilter(null);
                setGlobalSearchQuery("");
                clearFilters();
              }}
              className="ml-auto px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
              style={{
                backgroundColor: "#fee2e2",
                color: "#dc2626",
              }}
            >
              {t("clearAll")}
            </motion.button>
          </div>
        </motion.div>
      )}
      {(globalSearchQuery.length > 0 ||
        selectedCategoryFilter ||
        dietaryFilters.length > 0) && (
        <div className="container mx-auto px-4 py-6">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {isOnePageLayout ? (
                <RestaurantOnePageLayout
                  searchMode={true}
                  filteredItems={filteredItems}
                />
              ) : viewMode === "grid" ? (
                <MenuItemGrid
                  items={filteredItems}
                  theme={branchData?.branch.theme!}
                  setSelectedItem={setSelectedItem}
                />
              ) : (
                <MenuItemList
                  items={filteredItems}
                  theme={branchData?.branch.theme!}
                  setSelectedItem={setSelectedItem}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
      {(!globalSearchQuery || globalSearchQuery.length === 0) &&
        !selectedCategoryFilter &&
        dietaryFilters.length === 0 && (
          <main className="container mx-auto px-4 py-8 ">
            <AnimatePresence mode="wait">
              {activeTab === "menu" && (
                <motion.div
                  key="menu-tab"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {isOnePageLayout ? (
                    <RestaurantOnePageLayout />
                  ) : (
                    <RestaurantLayout />
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Persistent Tabs - kept mounted to avoid re-loading states (like maps/reviews) */}
            <div style={{ display: activeTab === "info" ? "block" : "none" }}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: activeTab === "info" ? 1 : 0,
                  y: activeTab === "info" ? 0 : 10,
                }}
                transition={{ duration: 0.3 }}
              >
                <RestaurantInfo restaurant={branchData.branch} />
              </motion.div>
            </div>

            <div
              style={{ display: activeTab === "feedback" ? "block" : "none" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: activeTab === "feedback" ? 1 : 0,
                  y: activeTab === "feedback" ? 0 : 10,
                }}
                transition={{ duration: 0.3 }}
              >
                <BranchFeedback />
              </motion.div>
            </div>

            <div
              style={{ display: activeTab === "location" ? "block" : "none" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: activeTab === "location" ? 1 : 0,
                  y: activeTab === "location" ? 0 : 10,
                }}
                transition={{ duration: 0.3 }}
              >
                <RestaurantLocation restaurant={branchData.branch} />
              </motion.div>
            </div>
          </main>
        )}

      {/* Shopping Cart Button */}
      <CartButton
        onItemClick={(item) => {
          setSelectedItem(item);
        }}
        position={isAgentActive ? "fixed bottom-22" : "fixed bottom-6"}
      />

      {/* Chatbot - appears on all tabs */}
      {isAgentActive && (
        <div
          onClick={() => setShowChatbotHint(false)}
          className={`fixed bottom-6 z-30 ${
            i18n.language === "ar" ? "left-6" : "right-6"
          }`}
        >
          {/* Animated Speech Bubble Hint */}
          <AnimatePresence>
            {showChatbotHint && (
              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.3,
                  x: i18n.language === "ar" ? -10 : 10,
                  y: -10,
                  rotate: i18n.language === "ar" ? 5 : -5,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  x: 0,
                  y: 0,
                  rotate: 0,
                }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                  x: i18n.language === "ar" ? -5 : 5,
                  y: -5,
                  rotate: i18n.language === "ar" ? -2 : 2,
                }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                  duration: 0.6,
                }}
                className={`absolute bottom-full mb-16 pointer-events-none ${
                  i18n.language === "ar" ? "left-0" : "right-0"
                }`}
                style={{
                  transformOrigin:
                    i18n.language === "ar" ? "bottom left" : "bottom right",
                }}
              >
                {/* Speech Bubble */}
                <motion.div
                  animate={{
                    y: [0, -3, 0],
                    rotate: [0, 1, -1, 0],
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 2,
                    ease: "easeInOut",
                  }}
                  className="relative bg-white rounded-2xl shadow-2xl px-4 py-3 w-48 sm:w-52 md:max-w-[200px] border"
                  style={{
                    backgroundColor:
                      branchData.branch.theme.cardColor || "#ffffff",
                    borderColor:
                      branchData.branch.theme.primaryColor || "#0ea5e9",
                    boxShadow: `0 10px 40px ${
                      branchData.branch.theme.primaryColor || "#0ea5e9"
                    }20`,
                    transform:
                      window.innerWidth < 640
                        ? i18n.language === "ar"
                          ? "translateX(100px)"
                          : "translateX(-100px)"
                        : i18n.language === "ar"
                          ? "translateX(80px)"
                          : "translateX(-80px)",
                  }}
                >
                  {/* Sparkle Animation */}
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.7, 1, 0.7],
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      ease: "easeInOut",
                    }}
                    className={`absolute -top-1 text-yellow-400 ${
                      i18n.language === "ar" ? "-right-1" : "-left-1"
                    }`}
                    style={{ fontSize: "16px" }}
                  ></motion.div>

                  <p
                    className="text-xs sm:text-sm font-medium leading-relaxed"
                    style={{
                      color: branchData.branch.theme.textColor || "#1e293b",
                      fontFamily:
                        branchData.branch.theme.fontFamily || "inherit",
                    }}
                  >
                    {randomHint}
                  </p>

                  {/* Speech Bubble Tail */}
                  <div
                    className={`absolute -bottom-2 ${
                      i18n.language === "ar"
                        ? "left-4 sm:left-6"
                        : "right-4 sm:right-6"
                    }`}
                  >
                    {/* Shadow/Border for the tail */}
                    <div
                      className="absolute w-0 h-0 border-l-6 border-r-6 border-t-6 sm:border-l-8 sm:border-r-8 sm:border-t-8 border-transparent"
                      style={{
                        borderTopColor:
                          branchData.branch.theme.primaryColor || "#0ea5e9",
                        opacity: 0.2,
                        transform: "translate(1px, 1px)",
                      }}
                    />
                    {/* Main tail */}
                    <div
                      className="w-0 h-0 border-l-6 border-r-6 border-t-6 sm:border-l-8 sm:border-r-8 sm:border-t-8 border-transparent"
                      style={{
                        borderTopColor:
                          branchData.branch.theme.cardColor || "#ffffff",
                        filter: `drop-shadow(0 1px 2px ${
                          branchData.branch.theme.primaryColor || "#0ea5e9"
                        }40)`,
                      }}
                    />
                  </div>

                  {/* Close button */}
                  <motion.button
                    onClick={() => setShowChatbotHint(false)}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.95 }}
                    className={`absolute -top-2.5 sm:-top-3 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg pointer-events-auto transition-all duration-200 ${
                      i18n.language === "ar"
                        ? "-right-2 sm:-right-2.5"
                        : "-left-2 sm:-left-2.5"
                    }`}
                    style={{
                      backgroundColor:
                        branchData.branch.theme.primaryColor || "#0ea5e9",
                      border: "2px solid white",
                      boxShadow: `0 4px 12px ${
                        branchData.branch.theme.primaryColor || "#0ea5e9"
                      }40`,
                    }}
                    aria-label="Close hint"
                    title="Close"
                  >
                    <FaX size={10} />
                  </motion.button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chatbot Component */}
          <Chatbot
            onItemClick={(item) => {
              setSelectedItem(item);
              setShowChatbotHint(false); // Hide hint when chatbot is clicked
            }}
          />
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        show={!!feedbackItem}
        onClose={() => setFeedbackItem(null)}
        item={feedbackItem || undefined}
        onSuccess={() => setShowFeedbackCongratulations(true)}
      />

      {/* Feedback Congratulations */}
      <FeedbackCongratulations
        show={showFeedbackCongratulations}
        onClose={() => setShowFeedbackCongratulations(false)}
        duration={5000}
      />
    </div>
  );
};

export default ModernRestaurantPage;
